import * as React from 'react';
import { useIntl } from 'react-intl';
import { useAuth } from '../../auth/AuthContext';
import { getPdfMeta, getPdfPageThumbnailUrl } from '../../services/pdfService';
import { presentationMessages } from '../../intl/messages';
import type { FileItem, SlideInfo } from './types';

interface UsePresentationSlidesResult {
  slides: SlideInfo[];
  loading: boolean;
  error: string | null;
  /** Refreshed original PPTX signed URL (may differ from the prop value). */
  originalSignedUrl: string | undefined;
}

export function usePresentationSlides(
  file: FileItem
): UsePresentationSlidesResult {
  const intl = useIntl();
  const { api } = useAuth();

  const [slides, setSlides] = React.useState<SlideInfo[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [originalSignedUrl, setOriginalSignedUrl] = React.useState<
    string | undefined
  >(file.originalSignedUrl);

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!api) {
        return;
      }
      setLoading(true);
      setError(null);

      try {
        const fsClient = api.client('fileSystem');
        const result = (await fsClient.find({
          query: `#${file.id}`,
          specs: {
            id: true,
            signedUrl: true,
            original: { signedUrl: true },
          },
        })) as {
          signedUrl?: string | null;
          original?: { signedUrl?: string | null };
        };

        if (cancelled) {
          return;
        }

        const nextPdfSignedUrl = result?.signedUrl ?? file.signedUrl;
        const pdfMeta = nextPdfSignedUrl
          ? await getPdfMeta(file.id, nextPdfSignedUrl)
          : undefined;

        if (cancelled) {
          return;
        }

        const slideCount = pdfMeta?.numPages ?? 0;

        const slideInfos: SlideInfo[] = Array.from(
          { length: slideCount },
          (_, i) => {
            const slideIndex = i + 1;
            return {
              index: slideIndex,
              thumbnailUrl: nextPdfSignedUrl
                ? getPdfPageThumbnailUrl(nextPdfSignedUrl, slideIndex)
                : undefined,
            };
          }
        );

        setOriginalSignedUrl(
          result?.original?.signedUrl ?? file.originalSignedUrl
        );
        setSlides(slideInfos);
      } catch (e) {
        if (cancelled) {
          return;
        }
        setError(
          e instanceof Error
            ? e.message
            : intl.formatMessage(presentationMessages.errorLoad)
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [api, file.id, file.originalSignedUrl, file.signedUrl, intl]);

  return { slides, loading, error, originalSignedUrl };
}
